import type { ChatInteraction } from '../../api/chat-history';
import type { BhanzuBuddyChatMessage } from '../../components/bhanzu-buddy-chat/bhanzu-buddy-chat-types';

/**
 * Message transformation utilities for BhanzuBuddy chat
 *
 * Converts API interactions to component messages with proper formatting
 * and state management.
 */

/**
 * Transform a single chat interaction to BhanzuBuddy chat messages
 */
export const transformInteractionToMessages = (
  interaction: ChatInteraction
): BhanzuBuddyChatMessage[] => {
  const timestampNumber = Number.parseInt(interaction.timestamp);

  // Determine feedback state from API response
  let feedbackGiven: 'positive' | 'negative' | null = null;
  if (interaction.positive_feedback === true) {
    feedbackGiven = 'positive';
  } else if (interaction.positive_feedback === false) {
    feedbackGiven = 'negative';
  }

  // Check if this is a QuickChallenge interaction
  const isQuickChallenge = !!(
    interaction.question_by_bb && interaction.answer_by_student
  );

  return isQuickChallenge
    ? [
        {
          id: `buddy-qc-${interaction.timestamp}`,
          type: 'buddy' as const,
          content: interaction.question_by_bb as string,
          timestamp: new Date(timestampNumber * 1000),
          isQuickChallenge: true, // Mark QC messages to hide feedback/guide
        },
        {
          id: `user-${interaction.timestamp}`,
          type: 'user' as const,
          content: interaction.question,
          timestamp: new Date(timestampNumber * 1000),
        },
        {
          id: `buddy-${interaction.timestamp}`,
          type: 'buddy' as const,
          content: interaction.answer,
          timestamp: new Date(timestampNumber * 1000),
          version: interaction.version,
          feedbackGiven,
          // Don't mark QC answer as isQuickChallenge - we want feedback/guide for the answer
        },
      ]
    : [
        {
          id: `user-${interaction.timestamp}`,
          type: 'user' as const,
          content: interaction.question,
          timestamp: new Date(timestampNumber * 1000),
        },
        {
          id: `buddy-${interaction.timestamp}`,
          type: 'buddy' as const,
          content: interaction.answer,
          timestamp: new Date(timestampNumber * 1000),
          version: interaction.version,
          feedbackGiven,
        },
      ];
};
